import { Component, input, ViewEncapsulation } from '@angular/core';
import { find, forEach } from 'lodash-es';

/**
 * Native port of the AngularJS `ftPodium` component (PodiumController) — the logical
 * wrapper around the winner/runner-up slots. Renders only projected content via a
 * transparent (`display: contents`) `<ng-content>` host. The editing state machine
 * (setWinner/setRunner/bumpDown/move/remove/swap/getPlace + `isPlaceTaken*`) mutates
 * `this.podium` in place. Slots reach this controller via a `#podium` template-ref input.
 */
@Component({
    selector: 'ft-podium-ng',
    imports: [],
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./ft-podium.component.scss'],
    templateUrl: './ft-podium.component.html'
})
export class FtPodiumComponent {
    readonly podium = input<any>();
    readonly compareBy = input('@photoUUID');
    readonly isEditable = input(false);


    setWinner(finalist: any, place: string): void {
        this.podium()[place] = finalist;
    }

    setRunner(finalist: any, index: number): void {
        this.podium().runnerUps.items[index] = finalist;
    }

    bumpDown(finalist: any, place: string): void {
        if (place === 'runner') throw 'Not supported';
        const podium = this.podium();
        if (place === 'second') {
            if (this.isDefined(podium.third)) podium.third = podium.second;
        } else if (place === 'first') {
            if (this.isDefined(podium.third)) podium.third = podium.second;
            if (this.isDefined(podium.second)) podium.second = podium.first;
        }
        podium[place] = finalist;
    }

    move(finalist: any, toPlace: string): void {
        if (toPlace === 'runner') {
            this.remove(finalist);
        } else {
            this.remove(finalist);
            this.podium()[toPlace] = finalist;
        }
    }

    remove(finalist: any): void {
        const place = this.getPlace(finalist);
        if (!place) return;
        const podium = this.podium();
        if (place !== 'runner') {
            podium[place] = null;
        } else {
            forEach(podium.runnerUps.items, (runner: any, index: number) => {
                if (this.areEquals(runner, finalist)) {
                    podium.runnerUps.items[index] = null;
                }
            });
        }
    }

    getPlace(finalist: any): string | null {
        const podium = this.podium();
        if (this.areEquals(podium.first, finalist)) return 'first';
        if (this.areEquals(podium.second, finalist)) return 'second';
        if (this.areEquals(podium.third, finalist)) return 'third';
        if (find(podium.runnerUps.items, (runner: any) => this.areEquals(runner, finalist))) return 'runner';
        return null;
    }

    swap(winnerA: any, winnerB: any): void {
        const placeA = this.getPlace(winnerA);
        const placeB = this.getPlace(winnerB);
        this.move(winnerA, placeB as string);
        this.move(winnerB, placeA as string);
    }

    isPlaceTaken(place: string): boolean {
        return place === 'runner' ? false : !!this.podium()[place];
    }

    isPlaceTakenByFinalist(finalist: any, place: string): boolean {
        return this.areEquals(this.podium()[place], finalist);
    }

    isExists(finalist: any): boolean {
        const podium = this.podium();
        return (
            this.areEquals(podium.first, finalist) ||
            this.areEquals(podium.second, finalist) ||
            this.areEquals(podium.third, finalist) ||
            !!find(podium.runnerUps.items, (runner: any) => this.areEquals(runner, finalist))
        );
    }

    /** v1 `areEquals` — identity OR equal `compareBy` keys. */
    private areEquals(first: any, second: any): boolean {
        if (first === second) return true;
        if (first != null && second != null) return first[this.compareBy()] === second[this.compareBy()];
        return false;
    }

    private isDefined(value: any): boolean {
        return typeof value !== 'undefined';
    }
}
